import { forwardRef } from 'react'

import logoSvg from '@/assets/logo.svg'
import { cn } from '@/lib/utils'
import { FxAvatar } from './FxAvatar'
import { FxIcon } from './FxIcon'

/**
 * The desktop navigation: an 80px white rail holding the logo, the create
 * action, the destinations and the account avatar.
 *
 * Ported from the design system's `AppRail`. Two things are looser here than
 * in the reference implementation, because the target screens need them: the
 * destination list can be replaced (`items` / `footerItems` — the artboards
 * put Einstellungen where the reference has the sidebar toggle), and an item
 * may carry an `href`, so a screen can render real links instead of buttons
 * where the destination is a URL.
 *
 * The rail is `hidden md:flex` in `FxNavShell`; below 992px the same
 * destinations appear in the top bar's drawer, and on phones in `FxBottomNav`.
 */

/** The destinations the shell knows about, plus the account. */
export type FxNavKey = 'home' | 'projects' | 'archive' | 'trash' | 'settings' | 'account'

export interface FxNavItem {
  key: string
  /** Material Symbols ligature name. Navigation icons use the filled cut. */
  icon: string
  /** The item's page name. The rail has no text, so this is its only label. */
  label: string
  /**
   * Where the item goes. Given, the item is an `<a>`; otherwise it is a
   * `<button>` and the consumer routes in `onNavigate`.
   */
  href?: string
}

/** Übersicht, Projekte, Archiv, Papierkorb — the reference implementation's set. */
export const FX_RAIL_ITEMS: FxNavItem[] = [
  { key: 'home', icon: 'home', label: 'Übersicht' },
  { key: 'projects', icon: 'folder', label: 'Projekte' },
  { key: 'archive', icon: 'archive', label: 'Archiv' },
  { key: 'trash', icon: 'delete', label: 'Papierkorb' },
]

const railButtonStyles =
  'flex h-control w-control shrink-0 items-center justify-center rounded-sm text-text-muted transition-colors hover:text-primary-text'

export interface FxRailButtonProps {
  item: FxNavItem
  active?: boolean
  /** The create action: `primary` at rest, and never a "you are here". */
  accent?: boolean
  onClick?: () => void
}

function RailButton({ item, active = false, accent = false, onClick }: FxRailButtonProps) {
  const className = cn(
    railButtonStyles,
    accent && 'text-primary hover:text-primary-pressed',
    active && 'bg-primary-strong text-on-primary shadow-raised hover:text-on-primary',
  )
  const icon = <FxIcon name={item.icon} size={accent ? 32 : 24} filled={!accent} />
  const shared = {
    className,
    title: item.label,
    'aria-label': item.label,
    'aria-current': active ? ('page' as const) : undefined,
  }
  return item.href ? (
    <a href={item.href} {...shared} onClick={onClick}>
      {icon}
    </a>
  ) : (
    <button type="button" {...shared} onClick={onClick}>
      {icon}
    </button>
  )
}

function Rule() {
  return <hr className="w-10 shrink-0 border-0 border-t-2 border-divider" />
}

export interface FxAppRailProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  active?: FxNavKey | string
  /** The logo. Defaults to the bundled mark, which is what every app uses. */
  logoSrc?: string
  /** Initials for the account avatar. Without them the avatar is a person glyph. */
  initials?: string
  /** An avatar element to render instead of the built-in circle (e.g. `FxAvatar`). */
  avatar?: React.ReactNode
  items?: FxNavItem[]
  /** Items below the spacer, above the avatar (Einstellungen on the artboards). */
  footerItems?: FxNavItem[]
  onNavigate?: (key: string) => void
  onCreate?: () => void
  /** Renders the sidebar toggle above the avatar. Omitted, the toggle is not drawn. */
  onToggle?: () => void
  /**
   * Replaces the built-in account button entirely.
   *
   * The built-in one is a `<button>` wrapping the avatar, which is right for
   * an account *destination* and impossible for an account *menu*: a menu
   * trigger is itself a button and must not nest inside one. A shell that
   * wants the avatar to open a menu passes the whole control here.
   */
  accountSlot?: React.ReactNode
}

export const FxAppRail = forwardRef<HTMLElement, FxAppRailProps>(
  (
    {
      active = 'home',
      logoSrc = logoSvg,
      initials,
      avatar,
      items = FX_RAIL_ITEMS,
      footerItems,
      onNavigate,
      onCreate,
      onToggle,
      accountSlot,
      className,
      ...props
    },
    ref,
  ) => {
    const go = (key: string) => () => onNavigate?.(key)
    return (
      <nav
        ref={ref}
        aria-label="Hauptnavigation"
        className={cn(
          'flex w-rail shrink-0 flex-col items-center gap-5 bg-surface py-5',
          className,
        )}
        {...props}
      >
        <img src={logoSrc} alt="formalogix" width={40} height={40} className="shrink-0" />
        <Rule />
        {/* Only an app that creates something draws the action — the staff
            console has nothing to create from here. */}
        {onCreate && (
          <RailButton
            item={{ key: 'create', icon: 'add', label: 'Neues Musterformular' }}
            accent
            onClick={onCreate}
          />
        )}
        {items.map((item) => (
          <RailButton
            key={item.key}
            item={item}
            active={active === item.key}
            onClick={go(item.key)}
          />
        ))}
        <span className="flex-1" />
        {footerItems?.map((item) => (
          <RailButton
            key={item.key}
            item={item}
            active={active === item.key}
            onClick={go(item.key)}
          />
        ))}
        {onToggle && (
          <RailButton
            item={{ key: 'toggle', icon: 'side_navigation', label: 'Seitenleiste' }}
            onClick={onToggle}
          />
        )}
        <Rule />
        {accountSlot ?? (
          <button
            type="button"
            aria-label="Account"
            aria-current={active === 'account' ? 'page' : undefined}
            className="shrink-0 rounded-full"
            onClick={go('account')}
          >
            {avatar ?? <FxRailAvatar initials={initials} filled={active === 'account'} />}
          </button>
        )}
      </nav>
    )
  },
)
FxAppRail.displayName = 'FxAppRail'

/**
 * The rail's account mark: the design system's `Avatar`, or — before anyone
 * is signed in and there are no initials to show — the same circle carrying
 * a person glyph, which `Avatar` itself does not offer.
 */
function FxRailAvatar({ initials, filled }: { initials?: string; filled?: boolean }) {
  if (initials) return <FxAvatar initials={initials} filled={filled} />
  return (
    <span
      className={cn(
        'inline-flex h-avatar w-avatar items-center justify-center rounded-full border-[1.5px] border-primary-strong',
        filled ? 'bg-primary-strong text-on-primary' : 'bg-surface text-primary-text',
      )}
    >
      <FxIcon name="person" size={24} filled />
    </span>
  )
}
